import {
  Box,
  Typography,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

interface OrganizationalAlignment {
  score: number;
  evidencedClaims: number;
  unsupportedClaims: number;
  contextDocumentsUsed: string[];
  alignmentIssues: Array<{
    claim: string;
    issue: string;
    suggestion: string;
  }>;
}

interface AlignmentDetailsProps {
  data: OrganizationalAlignment;
}

export default function AlignmentDetails({ data }: AlignmentDetailsProps) {
  return (
    <Stack spacing={2}>
      {/* Claims Summary */}
      <Box>
        <Typography variant="body2" gutterBottom>
          <strong>{data.evidencedClaims}</strong> claims supported by evidence
        </Typography>
        {data.unsupportedClaims > 0 && (
          <Typography variant="body2" color="warning.main" gutterBottom>
            <strong>{data.unsupportedClaims}</strong> claims lacking evidence
          </Typography>
        )}
      </Box>

      {/* Context Documents Used */}
      {data.contextDocumentsUsed.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Context Documents Referenced:
          </Typography>
          <List dense>
            {data.contextDocumentsUsed.map((doc, idx) => (
              <ListItem key={idx} sx={{ pl: 2 }}>
                <ListItemText
                  primary={`• ${doc}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Alignment Issues */}
      {data.alignmentIssues.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Alignment Issues:
          </Typography>
          <Stack spacing={1.5}>
            {data.alignmentIssues.map((issue, idx) => (
              <Alert key={idx} severity="warning">
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Claim: {issue.claim}
                  </Typography>
                  <Typography variant="body2" color="error">
                    Issue: {issue.issue}
                  </Typography>
                  <Typography variant="body2" color="primary">
                    Suggestion: {issue.suggestion}
                  </Typography>
                </Stack>
              </Alert>
            ))}
          </Stack>
        </Box>
      )}

      {/* Success Message */}
      {data.alignmentIssues.length === 0 && data.evidencedClaims > 0 && (
        <Alert severity="success">
          All claims are well-supported by organizational context documents.
        </Alert>
      )}
    </Stack>
  );
}
